/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Who owns the client.
 */
export const ServerClientAssociationScope = {
  Project: "project",
  Organization: "organization",
  Global: "global",
} as const;
/**
 * Who owns the client.
 */
export type ServerClientAssociationScope = ClosedEnum<
  typeof ServerClientAssociationScope
>;

/**
 * One upstream OAuth client an MCP server is associated with. Client secrets are never returned.
 */
export type ServerClientAssociation = {
  /**
   * When the association was recorded.
   */
  associatedAt: Date;
  /**
   * The provider-issued client_id.
   */
  clientId: string;
  /**
   * Display name of the remote session issuer, if set.
   */
  issuerName?: string | undefined;
  /**
   * Slug of the remote session issuer.
   */
  issuerSlug: string;
  /**
   * The remote session issuer's issuer identifier.
   */
  issuerUrl: string;
  /**
   * The associated remote session client.
   */
  remoteSessionClientId: string;
  /**
   * The client's remote session issuer: the upstream authorization server it signs in with.
   */
  remoteSessionIssuerId: string;
  /**
   * Who owns the client.
   */
  scope: ServerClientAssociationScope;
  /**
   * How many live MCP servers are associated with this client, including this one.
   */
  serverCount: number;
};

/** @internal */
export const ServerClientAssociationScope$inboundSchema: z.ZodMiniEnum<
  typeof ServerClientAssociationScope
> = z.enum(ServerClientAssociationScope);

/** @internal */
export const ServerClientAssociation$inboundSchema: z.ZodMiniType<
  ServerClientAssociation,
  unknown
> = z.pipe(
  z.object({
    associated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    client_id: z.string(),
    issuer_name: z.optional(z.string()),
    issuer_slug: z.string(),
    issuer_url: z.string(),
    remote_session_client_id: z.string(),
    remote_session_issuer_id: z.string(),
    scope: ServerClientAssociationScope$inboundSchema,
    server_count: z.int(),
  }),
  z.transform((v) => {
    return remap$(v, {
      "associated_at": "associatedAt",
      "client_id": "clientId",
      "issuer_name": "issuerName",
      "issuer_slug": "issuerSlug",
      "issuer_url": "issuerUrl",
      "remote_session_client_id": "remoteSessionClientId",
      "remote_session_issuer_id": "remoteSessionIssuerId",
      "server_count": "serverCount",
    });
  }),
);

export function serverClientAssociationFromJSON(
  jsonString: string,
): SafeParseResult<ServerClientAssociation, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ServerClientAssociation$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ServerClientAssociation' from JSON`,
  );
}
